// eslint-disable-next-line import/prefer-default-export
import { parseCookies } from "nookies";
import {
  loginFB, internalLogin, getActivePackage
} from "./api";
import link from "../static/text/link";
import { getOwnUserProfile } from "./cookie";
import { getInternalProfile, loginInternalGoogle } from "./internalRequest";
import { buildPaymentRedirectUrl } from "./paymentRedirect";

export const handleLoginRedirect = async (
  packageId,
  languageCode,
  loginData,
  isGoogle,
  promo_code,
  promo_price,
  paymentQuery = {}
) => {
  let finalRedirect = link.mobile.accountOverview;
  if (!loginData.is_verified_email && !isGoogle && !packageId) {
    finalRedirect = `${link.mobile.accountOtp}?language_code=${languageCode}`;
  } else if ((loginData.welcome_step === 1 || loginData.welcome_step === 2) && !packageId) {
    finalRedirect = `${link.mobile.completeRegistrationData}`;
  } else if (loginData.fill_name && !packageId) {
    finalRedirect = `${link.mobile.fillName
      }?language_code=${languageCode}${packageId ? `&package_id=${packageId}` : ""
      }`;
  } else {
    const authCookie = parseCookies().authorization;
    let profile = { data: {} };
    try {
      const parsed = authCookie ? JSON.parse(authCookie) : {};
      profile = await getInternalProfile({
        authorization: parsed.access_token,
      });
    } catch (profileErr) {
      window.location.href = link.mobile.accountOverview;
      return true;
    }
    const userData = profile.data;
    if (
      !userData.not_complete
      && !userData.is_active
      && userData.workout_premium_status
    ) {
      const invoicePackage = await getActivePackage();
      finalRedirect = `${link.mobile.otp}?invoice_id=${invoicePackage.data.invoice.id}&language_code=${languageCode}`;
    } else if (packageId) {
      finalRedirect = buildPaymentRedirectUrl(packageId, languageCode, {
        ...paymentQuery,
        promo_code: promo_code || paymentQuery.promo_code,
        active_promo: promo_code || paymentQuery.active_promo,
        promo_price: promo_price || paymentQuery.promo_price,
      });
    } else {
      finalRedirect = link.mobile.accountOverview;
    }
  }
  window.location.href = finalRedirect;
  return true;
};

export const setUserToStorage = async () => {
  const user = await getOwnUserProfile();
  localStorage.setItem("userInfo", JSON.stringify(user));
  return true;
};

export const removeUserFromStorage = () => {
  localStorage.removeItem("userInfo");
};

export const getUserData = () => {
  const raw = localStorage.getItem("userProfile");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // helpers/cookie.js writeUserProfileLocal wraps profile as { data, ts }.
    // Older writers store the raw user object directly. Support both.
    if (parsed && parsed.data && typeof parsed.data === "object") {
      return parsed.data;
    }
    return parsed;
  } catch (e) {
    return null;
  }
};

// Trello 437: a session that is authenticated only because registration stored
// a token must not reach Account Overview. Returns the URL of the registration
// step the profile belongs to (mirroring handleLoginRedirect's routing), or
// null when the profile is fully registered / carries no account-state flags.
//
// Flag semantics differ by payload source:
// - oauth/member profile: is_active/is_verified_email are always present
//   (bool cast / "yes"/"no" string), welcome_step is the adjusted value.
// - login payload in userProfile: same flags, raw values.
// - register payload in userProfile: the bare authorization grant — a real
//   access_token plus id/email but none of the flags — which only exists
//   before the first activation, so it is itself an incomplete signal.
// Profiles with NO recognizable state (blanked-token cookie writes, legacy
// cookie migrations) return null so existing sessions keep working.
export const getRegistrationStepRedirect = (user, languageCode) => {
  if (!user || typeof user !== "object") return null;
  const langQuery = languageCode ? `?language_code=${languageCode}` : "";
  // oauth/member serializes is_verified_email as the 'Yes'/'No' label —
  // match it case-insensitively; the login payload carries the raw bool/int.
  const emailUnverified = user.is_verified_email === false
    || user.is_verified_email === 0
    || String(user.is_verified_email).toLowerCase() === "no";
  const bareAuthorization = !!user.access_token
    && typeof user.is_active === "undefined"
    && typeof user.is_verified_email === "undefined"
    && typeof user.welcome_step === "undefined";
  // Trello 548 [web - otp]: is_active must NOT gate this page. The web
  // registration chain (register -> OTP activate -> welcome/step1) never
  // flips users.is_active — the backend only sets it at the app's welcome
  // wizard (welcome/next -> step 5 / location) or on email-change confirm —
  // so a fully web-registered member still reads is_active=false and would
  // be bounced back to OTP forever. "Needs OTP" on web means the email is
  // unverified (pending activations row) or the bare register grant.
  if (emailUnverified || bareAuthorization) {
    return `${link.mobile.accountOtp}${langQuery}`;
  }
  if (user.welcome_step === 1 || user.welcome_step === 2) {
    return link.mobile.completeRegistrationData;
  }
  if (user.fill_name === true) {
    return `${link.mobile.fillName}${langQuery}`;
  }
  return null;
};

export const handlePaymentSession = async (
  data,
  packageId,
  languageCode,
  isGoogle,
  promo_code,
  promo_price,
  paymentQuery = {}
) => {
  localStorage.setItem("userProfile", JSON.stringify(data));
  await handleLoginRedirect(
    packageId,
    languageCode,
    data,
    isGoogle,
    promo_code,
    promo_price,
    paymentQuery
  );
  return true;
};

export const customSigninRedirect = (page_success) => {
  switch (page_success) {
    case "email-preference":
      window.location.href = "/email-preference";
      break;
    default:
      break;
  }
};

export const handleRegisterSession = async (data, languageCode) => {
  localStorage.setItem("userProfile", JSON.stringify(data));
  await internalLogin(data);
  window.location.href = `${link.mobile.accountOtp}?language_code=${languageCode}`;
  return true;
};

export const handleGoogleLoginSuccess = async (
  googleId,
  languageCode = "en",
  packageId,
  promo_price,
  pageSuccess,
  paymentQuery = {}
) => {
  try {
    const body = {
      token: googleId.accessToken,
      language_code: languageCode,
    };
    const loginResp = await loginInternalGoogle(body);
    if (pageSuccess && pageSuccess !== "undefined") {
      customSigninRedirect(pageSuccess);
      localStorage.removeItem("page_success");
    } else {
      await handlePaymentSession(
        loginResp,
        packageId,
        languageCode,
        true,
        undefined,
        promo_price,
        paymentQuery
      );
    }
  } catch (error) {
    return false;
  }
  return true;
};

export const handleGoogleLoginLandingSuccess = async (
  googleId,
  languageCode = "en",
  additionalBody = {}
) => {
  const body = {
    token: googleId.accessToken,
    language_code: languageCode,
    ...additionalBody,
  };

  const loginResp = await loginInternalGoogle(body);
  localStorage.setItem("userProfile", JSON.stringify(loginResp));
  return loginResp;
};

export const handleGoogleLoginFailure = (failureArgument) => failureArgument;

export const handleFacebookLoginSuccess = async (
  facebookId,
  languageCode = "en",
  packageId,
  paymentQuery = {}
) => {
  const body = {
    email: facebookId.email,
    token: facebookId.accessToken,
    language_code: languageCode,
  };
  const loginResp = await loginFB(body);
  await handlePaymentSession(loginResp.data, packageId, languageCode, false, undefined, undefined, paymentQuery);
  return true;
};

export const handleFacebookLoginFailure = (failureArgument) => failureArgument;

export const configurations = {
  facebookFields: "name,email,picture",
};
